/**
 * @fileoverview Contacts module (formKind === 'module', moduleType === 'contacts').
 * @module inspection-renderer/modules
 *
 * Responsibilities:
 * - Render the inspector-facing contact list per `ContactsModuleConfig`:
 *   show contacts grouped by role, allow add/edit/delete per `allowAddNew`
 *   / `allowSelectExisting`, and emit `ContactChange[]` upstream for the
 *   adapter to translate into canonical contact deltas (Phase 5).
 * - G-002 — module data contract: this module owns the contact list during
 *   the inspection event. Host injects an initial pool; module emits all
 *   changes.
 *
 * Dependencies:
 * - React 18 (peer).
 * - `@firesquirrel/core-domain` `ContactsModuleConfig`.
 * - `ContactChange` renderer-local type.
 *
 * Architecture:
 * - Presentation-only. Reads/writes flow through `contacts` + `onChange`.
 *
 * @component ContactsModule
 *
 * @contract ContactEntry
 * Purpose: renderer-local contact row. Hosts that already have a richer
 * contact record (mobile-pwa / tenant-web) project into this shape.
 *
 * @contract ContactsModuleProps
 * Purpose: prop contract for the contacts module.
 */
import { useState, useRef, type ReactNode } from 'react';
import type { ContactsModuleConfig, ContactEntry } from '@firesquirrel/core-domain';
import type { ContactChange } from '../violations/types';

export interface ContactsModuleProps {
  config: ContactsModuleConfig;
  contacts: ContactEntry[];
  readOnly?: boolean;
  /** Emits the change deltas (not the new contact list) so the host adapter
   *  can render the Data slide-in change log (Phase 6) without recomputing diffs. */
  onChange: (nextContacts: ContactEntry[], changes: ContactChange[]) => void;
}


function genId(): string {
  return `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function ContactsModule(props: ContactsModuleProps): ReactNode {
  const { config, contacts, readOnly, onChange } = props;
  const [draftRole, setDraftRole] = useState<string>(config.roles[0]?.role ?? '');
  const [draftName, setDraftName] = useState<string>('');
  const nameInputRef = useRef<HTMLInputElement>(null);

  const counts = countsByRole(contacts);

  function add() {
    if (readOnly || !config.allowAddNew || !draftName.trim() || !draftRole) return;
    const c: ContactEntry = {
      id: genId(),
      name: draftName.trim(),
      roles: [draftRole],
      phones: [],
      emails: [],
      phone: '',
      email: '',
    };
    const next = [...contacts, c];
    onChange(next, [{ kind: 'added', contactId: c.id, name: c.name, roles: c.roles, changedAt: new Date().toISOString() }]);
    setDraftName('');
  }

  function remove(id: string) {
    if (readOnly) return;
    const target = contacts.find((c) => c.id === id);
    if (!target) return;
    const next = contacts.filter((c) => c.id !== id);
    onChange(next, [{ kind: 'deleted', contactId: id, name: target.name, roles: target.roles, changedAt: new Date().toISOString() }]);
  }

  return (
    <div
      data-module-type="contacts"
      data-read-only={readOnly ? 'true' : 'false'}
      className="border border-gray-200 rounded-sm p-3"
    >
      <h3 className="text-sm font-semibold text-gray-800 mb-2">Contacts</h3>

      <ul role="list" data-contact-list className="flex flex-col gap-1.5">
        {contacts.map((c) => (
          <li key={c.id} role="listitem" data-contact-id={c.id} className="flex items-center justify-between text-xs">
            <div>
              <span className="font-medium">{c.name}</span>
              <span className="ml-2 text-gray-500">— {c.roles.join(', ')}</span>
            </div>
            {!readOnly && (
              <button type="button" onClick={() => remove(c.id)} className="text-xs text-red-500 hover:text-red-700">Remove</button>
            )}
          </li>
        ))}
      </ul>

      {/* Per-role count + warning if below minRequired + quick-add "+" (DFE-015). */}
      <div data-role-summary className="mt-2 text-[11px] text-gray-600">
        {config.roles.map((rc) => {
          const got = counts.get(rc.role) ?? 0;
          const short = got < rc.minRequired;
          return (
            <span key={rc.role} data-role={rc.role} data-role-short={short ? 'true' : 'false'} className={`mr-3 inline-flex items-center gap-0.5 ${short ? 'text-red-600' : ''}`}>
              {rc.role}: {got}/{rc.minRequired}
              {!readOnly && config.allowAddNew && short && (
                <button
                  type="button"
                  data-action={`quick-add-${rc.role}`}
                  onClick={() => {
                    setDraftRole(rc.role);
                    setDraftName('');
                    nameInputRef.current?.focus();
                  }}
                  className="ml-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white text-[10px] leading-none hover:bg-blue-700"
                  title={`Add ${rc.role}`}
                >
                  +
                </button>
              )}
            </span>
          );
        })}
      </div>

      {!readOnly && config.allowAddNew && (
        <div className="mt-2 flex gap-2 items-center">
          <select
            value={draftRole}
            onChange={(e) => setDraftRole(e.target.value)}
            data-action="draft-role"
            className="px-2 py-1 text-xs border border-gray-300 rounded-sm"
          >
            {config.roles.map((r) => (
              <option key={r.role} value={r.role}>{r.role}</option>
            ))}
          </select>
          <input
            ref={nameInputRef}
            type="text"
            value={draftName}
            placeholder="Name"
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            data-action="draft-name"
            className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-sm"
          />
          <button
            type="button"
            onClick={add}
            disabled={!draftName.trim() || !draftRole}
            data-action="add-contact"
            className="px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-sm hover:bg-blue-700 disabled:bg-gray-300"
          >
            Add
          </button>
        </div>
      )}
    </div>
  );
}

function countsByRole(contacts: ContactEntry[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const c of contacts) {
    for (const role of c.roles) {
      m.set(role, (m.get(role) ?? 0) + 1);
    }
  }
  return m;
}
